// ===========================================
// Enquiry flags (enquiries plan §10): ONE label map for the seven keys
// ===========================================
// The server decides which flags a row carries (`flags` on the list row and the detail); the
// browser only names and colours them. `ENQUIRY_FLAG_KEY_LIST` mirrors `ENQUIRY_FLAG_KEYS` (BE
// `enquirySettings.ts`) in the same order; `enquirySettings.parity.test.ts` pins the list and the
// `EnquiryFlagKey` type against it.

import type { EnquiryFlagKey } from '../api/enquiry.api';
import { brandColors, statusColors } from '../styles/brandColors';

/** In the server's order (`ENQUIRY_FLAG_KEYS`). */
export const ENQUIRY_FLAG_KEY_LIST: readonly EnquiryFlagKey[] = [
  'MNG_NOTES_MISSING',
  'NEW_UNCONTACTED',
  'NO_ACTIVITY',
  'OVERDUE',
  'HOT_QUIET',
  'ONBOARDING_INCOMPLETE',
  'NO_OWNER',
  'WAITLIST_REVIEW',
];

export const ENQUIRY_FLAG_LABELS: Record<EnquiryFlagKey, string> = {
  MNG_NOTES_MISSING: 'Notes missing',
  NEW_UNCONTACTED: 'Not contacted',
  NO_ACTIVITY: 'No activity',
  OVERDUE: 'Overdue',
  HOT_QUIET: 'Hot, gone quiet',
  // Phase 6: a linked student's check-in is past its due day with the checklist unfinished.
  ONBOARDING_INCOMPLETE: 'Onboarding overdue',
  NO_OWNER: 'No owner',
  WAITLIST_REVIEW: 'Waitlist review',
};

/** Flat chip colours with dark text (WCAG AA), from the brand tokens. */
export const ENQUIRY_FLAG_COLORS: Record<EnquiryFlagKey, { bg: string; text: string }> = {
  MNG_NOTES_MISSING: { bg: statusColors.warningBg, text: brandColors.textPrimary },
  NEW_UNCONTACTED: { bg: statusColors.infoBg, text: brandColors.textPrimary },
  NO_ACTIVITY: { bg: statusColors.pendingBg, text: brandColors.textPrimary },
  OVERDUE: { bg: statusColors.warningBg, text: brandColors.textPrimary },
  HOT_QUIET: { bg: statusColors.warningBg, text: brandColors.textPrimary },
  ONBOARDING_INCOMPLETE: { bg: statusColors.pendingBg, text: brandColors.textPrimary },
  NO_OWNER: { bg: statusColors.infoBg, text: brandColors.textPrimary },
  WAITLIST_REVIEW: { bg: statusColors.warningBg, text: brandColors.textPrimary },
};

export const isEnquiryFlagKey = (value: unknown): value is EnquiryFlagKey =>
  typeof value === 'string' && (ENQUIRY_FLAG_KEY_LIST as readonly string[]).includes(value);

/** Flags the school can switch on or off: all of them since Phase 6 ("Onboarding overdue"). */
export const SWITCHABLE_ENQUIRY_FLAGS: readonly EnquiryFlagKey[] = ENQUIRY_FLAG_KEY_LIST;

/** A line of help under a flag's switch, for the flags whose name alone does not say when they fire. */
export const ENQUIRY_FLAG_HELP: Partial<Record<EnquiryFlagKey, string>> = {
  WAITLIST_REVIEW: "A waitlisted Meet & Greet's review is due, or within the warning days. Only the owner can extend the review or confirm the waitlist lapsed.",
};
